/**
 * AutoAlertAffiliateViaEmailInAppPage — affiliate alert rules + recent alert feed.
 * GREEN-only accent palette. No emerald/blue/orange/purple/dark variants.
 *
 * Sections:
 *   1. Rule matrix — per alert kind, toggle email/in-app channel enablement
 *      (client-side config; BE has no rules endpoint yet — these drive the
 *      channel defaults of the create form).
 *   2. Create alert — trigger event form (kind + affiliate + channels subset of enabled).
 *   3. Recent alerts — reverse-chronological feed with mark-delivered toggle.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAffiliateAlertList,
	useAffiliateAlertCreate,
	useAffiliateAlertMarkDelivered,
} from "./hooks";
import type { AlertChannel, AlertKind } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_ON =
	"inline-flex items-center rounded-[6px] border border-green-700 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800";
const PILL_OFF =
	"inline-flex items-center rounded-[6px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600";

const KINDS: AlertKind[] = [
	"approval",
	"rejection",
	"commission",
	"payout",
	"offer_assigned",
	"offer_paused",
	"tier_change",
	"message",
];

const CHANNELS: AlertChannel[] = ["email", "in_app"];

const CHANNEL_LABEL: Record<AlertChannel, string> = {
	email: "Email",
	in_app: "In-app",
};

const KIND_LABEL: Record<AlertKind, string> = {
	approval: "Approval",
	rejection: "Rejection",
	commission: "Commission",
	payout: "Payout",
	offer_assigned: "Offer assigned",
	offer_paused: "Offer paused",
	tier_change: "Tier change",
	message: "Message",
};

type RuleMatrix = Record<AlertKind, Record<AlertChannel, boolean>>;

function initialRules(): RuleMatrix {
	const r = {} as RuleMatrix;
	for (const k of KINDS) {
		r[k] = { email: true, in_app: true };
	}
	return r;
}

function fmtTime(secs: number): string {
	return new Date(secs * 1000).toISOString().replace("T", " ").slice(0, 19);
}

export function AutoAlertAffiliateViaEmailInAppPage() {
	const listQuery = useAffiliateAlertList({ limit: 100 });
	const createMut = useAffiliateAlertCreate();
	const deliveredMut = useAffiliateAlertMarkDelivered();

	const [rules, setRules] = useState<RuleMatrix>(() => initialRules());
	const [affiliateId, setAffiliateId] = useState("");
	const [kind, setKind] = useState<AlertKind>("approval");
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");

	const enabledChannels = useMemo<AlertChannel[]>(() => {
		const row = rules[kind];
		return CHANNELS.filter((c) => row[c]);
	}, [rules, kind]);

	const toggleRule = (k: AlertKind, c: AlertChannel) => {
		setRules((prev) => ({
			...prev,
			[k]: { ...prev[k], [c]: !prev[k][c] },
		}));
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!affiliateId.trim() || enabledChannels.length === 0) return;
		const event_id = `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
		createMut.mutate(
			{
				event_id,
				affiliate_id: affiliateId.trim(),
				kind,
				channels: enabledChannels,
				subject: subject.trim() || undefined,
				body: body.trim() || undefined,
			},
			{
				onSuccess: () => {
					setSubject("");
					setBody("");
				},
			},
		);
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="auto-alert-affiliate-via-email-in-app-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					auto-alert-affiliate-via-email-in-app
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Affiliate alert rules
				</h1>
				<p className="text-sm text-zinc-600">
					Choose which lifecycle events reach affiliates, and on which
					channels. Triggered alerts appear in the recent feed below and
					can be marked delivered once dispatched.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Alert rules matrix"
				data-testid="auto-alert-affiliate-via-email-in-app-rules"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Rules
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Toggle a channel off to skip that alert kind on that channel.
				</p>
				<div className="mt-3 overflow-x-auto">
					<table className="w-full border-collapse text-sm">
						<thead>
							<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="py-2 pr-4 font-medium">
									Trigger event
								</th>
								{CHANNELS.map((c) => (
									<th
										key={c}
										className="py-2 pr-4 font-medium"
										scope="col"
									>
										{CHANNEL_LABEL[c]}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{KINDS.map((k) => (
								<tr
									key={k}
									className="border-b border-zinc-100 last:border-b-0"
									data-testid={`auto-alert-affiliate-via-email-in-app-rule-row-${k}`}
								>
									<td className="py-2 pr-4 text-zinc-800">
										{KIND_LABEL[k]}
									</td>
									{CHANNELS.map((c) => {
										const on = rules[k][c];
										return (
											<td key={c} className="py-2 pr-4">
												<button
													type="button"
													className={on ? PILL_ON : PILL_OFF}
													aria-pressed={on}
													onClick={() => toggleRule(k, c)}
													data-testid={`auto-alert-affiliate-via-email-in-app-toggle-${k}-${c}`}
												>
													{on ? "Enabled" : "Disabled"}
												</button>
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Trigger alert"
				data-testid="auto-alert-affiliate-via-email-in-app-create"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Trigger alert
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 flex flex-col gap-3"
					data-testid="auto-alert-affiliate-via-email-in-app-create-form"
				>
					<div className="grid gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
								Affiliate ID
							</span>
							<input
								type="text"
								className={INPUT}
								value={affiliateId}
								onChange={(e) => setAffiliateId(e.currentTarget.value)}
								disabled={createMut.isPending}
								placeholder="aff_123"
								data-testid="auto-alert-affiliate-via-email-in-app-aff-input"
							/>
						</label>
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
								Event kind
							</span>
							<select
								className={INPUT}
								value={kind}
								onChange={(e) =>
									setKind(e.currentTarget.value as AlertKind)
								}
								disabled={createMut.isPending}
								data-testid="auto-alert-affiliate-via-email-in-app-kind-select"
							>
								{KINDS.map((k) => (
									<option key={k} value={k}>
										{KIND_LABEL[k]}
									</option>
								))}
							</select>
						</label>
					</div>
					<label className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
							Subject (template ref, optional)
						</span>
						<input
							type="text"
							className={INPUT}
							value={subject}
							onChange={(e) => setSubject(e.currentTarget.value)}
							disabled={createMut.isPending}
							placeholder="tmpl.approval.v1"
							data-testid="auto-alert-affiliate-via-email-in-app-subject-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
							Body (optional)
						</span>
						<textarea
							rows={3}
							className={INPUT}
							value={body}
							onChange={(e) => setBody(e.currentTarget.value)}
							disabled={createMut.isPending}
							placeholder="Welcome to the program"
							data-testid="auto-alert-affiliate-via-email-in-app-body-input"
						/>
					</label>
					<div className="flex flex-wrap items-center gap-3 text-xs text-zinc-600">
						<span className="font-medium uppercase tracking-wide text-zinc-500">
							Channels
						</span>
						<span
							data-testid="auto-alert-affiliate-via-email-in-app-channels-preview"
						>
							{enabledChannels.length === 0
								? "none enabled for this kind"
								: enabledChannels
										.map((c) => CHANNEL_LABEL[c])
										.join(" + ")}
						</span>
					</div>
					<div>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!affiliateId.trim() ||
								enabledChannels.length === 0
							}
							data-testid="auto-alert-affiliate-via-email-in-app-submit"
						>
							{createMut.isPending ? "Sending" : "Send alert"}
						</button>
					</div>
					{createMut.error ? (
						<p
							className="text-xs text-red-700"
							role="alert"
							data-testid="auto-alert-affiliate-via-email-in-app-create-error"
						>
							{createMut.error.message}
						</p>
					) : null}
				</form>
			</section>

			<section
				className={CARD}
				aria-label="Recent alerts"
				data-testid="auto-alert-affiliate-via-email-in-app-feed"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Recent alerts
				</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-alert-affiliate-via-email-in-app-loading"
					>
						Loading alerts
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-700"
						role="alert"
						data-testid="auto-alert-affiliate-via-email-in-app-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-alert-affiliate-via-email-in-app-empty"
					>
						No alerts yet. Triggered alerts will appear here.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="auto-alert-affiliate-via-email-in-app-list"
					>
						{items.map((row) => {
							const delivered = row.delivered_at !== null;
							return (
								<li
									key={row.id}
									className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
									data-testid={`auto-alert-affiliate-via-email-in-app-row-${row.event_id}`}
								>
									<div className="flex flex-col gap-1">
										<div className="flex flex-wrap items-center gap-2 text-sm text-zinc-800">
											<span className="font-medium">
												{KIND_LABEL[row.kind] ?? row.kind}
											</span>
											<span className="text-xs text-zinc-500">
												to
											</span>
											<span className="font-mono text-xs text-zinc-700">
												{row.affiliate_id}
											</span>
										</div>
										<div className="flex flex-wrap items-center gap-1.5">
											{row.channels.map((c) => (
												<span
													key={c}
													className={PILL_ON}
													data-testid={`auto-alert-affiliate-via-email-in-app-chan-${row.event_id}-${c}`}
												>
													{CHANNEL_LABEL[c] ?? c}
												</span>
											))}
											<span
												className={delivered ? PILL_ON : PILL_OFF}
												data-testid={`auto-alert-affiliate-via-email-in-app-status-${row.event_id}`}
											>
												{delivered ? "Delivered" : "Pending"}
											</span>
										</div>
										<div className="text-xs text-zinc-500">
											<span className="font-mono">
												{row.event_id}
											</span>
											<span className="px-1">·</span>
											<span>{fmtTime(row.created_at)}</span>
											{delivered && row.delivered_at !== null ? (
												<>
													<span className="px-1">·</span>
													<span>
														delivered {fmtTime(row.delivered_at)}
													</span>
												</>
											) : null}
										</div>
									</div>
									<div>
										<button
											type="button"
											className={BTN_GHOST}
											onClick={() =>
												deliveredMut.mutate(row.event_id)
											}
											disabled={
												delivered || deliveredMut.isPending
											}
											data-testid={`auto-alert-affiliate-via-email-in-app-deliver-${row.event_id}`}
										>
											{delivered ? "Delivered" : "Mark delivered"}
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AutoAlertAffiliateViaEmailInAppPage;
